import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { setLanguage, getLanguage } from "@/lib/translator";

export const INR = (n) =>
  typeof n === "number" ? "\u20b9" + n.toLocaleString("en-IN") : n;
export const UNIT_LABEL = { "one-time": "one-time", month: "/mo", year: "/yr", hour: "/hr", custom: "" };

const NAV = [
  { to: "/kerala", label: "Home", end: true },
  { to: "/kerala/services", label: "Services" },
  { to: "/kerala/pricing", label: "Pricing" },
  { to: "/kerala/gallery", label: "Gallery" },
  { to: "/kerala/audit", label: "Website Audit" },
  { to: "/kerala/partners", label: "Partners" },
  { to: "/kerala/contact", label: "Get a Quote" },
];

function LangToggle({ className = "" }) {
  const [lang, setLang] = useState(getLanguage());
  const toggle = () => { const next = lang === "ml" ? "en" : "ml"; setLang(next); setLanguage(next); };
  return (
    <button onClick={toggle} data-testid="kerala-lang-toggle" data-no-translate
      className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border px-3 py-1.5 transition-colors ${lang === "ml" ? "bg-emerald-700 text-white border-emerald-700" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"} ${className}`}>
      <Languages size={14} /> {lang === "ml" ? "English" : "\u0d2e\u0d32\u0d2f\u0d3e\u0d33\u0d02"}
    </button>
  );
}

// Kerala.com base tenant shell — own brand, own nav/footer. No links to other instances.
export default function KeralaLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow"; m.setAttribute("data-kerala", "1");
    document.head.appendChild(m);
    return () => { document.head.querySelector('meta[data-kerala="1"]')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Green banner — Kerala, India */}
      <div data-no-translate className="bg-[#012a22] text-emerald-100 text-xs text-center py-1.5 tracking-wide">
        <span className="font-semibold text-emerald-300">Kerala</span> · India — Your local digital growth partner
      </div>
      <header className="sticky top-0 inset-x-0 z-50 glass-nav border-b border-emerald-900/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/kerala" data-testid="kerala-logo" className="flex items-center gap-2" data-no-translate>
            <div className="w-9 h-9 rounded-lg bg-[#024E3F] flex items-center justify-center text-white font-heading font-bold">K</div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-slate-900 text-sm">Kerala<span className="text-emerald-700">.com</span></p>
              <p className="text-[10px] text-slate-400 -mt-0.5">Digital Growth Partner</p>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-5">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`kerala-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `text-sm font-semibold transition-colors ${isActive ? "text-emerald-700" : "text-slate-600 hover:text-emerald-700"}`}>
                {n.label}
              </NavLink>
            ))}
            <LangToggle />
          </nav>
          <div className="flex items-center gap-2 lg:hidden">
            <LangToggle />
            <button onClick={() => setOpen(!open)} className="text-slate-700" data-testid="kerala-menu-toggle">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-3 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className="text-sm font-semibold text-slate-700">{n.label}</NavLink>
            ))}
          </div>
        )}
      </header>

      <main><Outlet /></main>

      <footer className="bg-[#024E3F] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <p className="font-heading font-bold text-lg">Kerala<span className="text-emerald-300">.com</span></p>
            <p className="text-emerald-100/70 text-sm mt-2 max-w-xs">Websites, AI & digital growth for Kerala businesses — and across India. Built in Malayalam & English.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-3">Explore</p>
            <ul className="space-y-2 text-sm text-emerald-100/80">
              {NAV.slice(1).map((n) => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-3">Kerala</p>
            <p className="text-sm text-emerald-100/80">₹ INR pricing · GST-ready invoicing · UPI & Indian gateways · WhatsApp support · Malayalam & English.</p>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-emerald-100/50">
          © {new Date().getFullYear()} Kerala.com · Your local digital growth partner
        </div>
      </footer>
    </div>
  );
}
